import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_CHECKS,
  RECEIVE_ALL_CHECKS,
  RECEIVING_CHECKS,
  RECEIVE_CHECK_METADATA,
  INCREMENT_CHECK_COUNT,
  RECEIVE_CHECK_FAILURE,
  SAVING_CHECK,
  SAVING_CHECK_FAILURE,
  UPDATING_CHECK,
  UPDATING_CHECK_FAILURE,
  DELETE_CHECK,
  DELETING_CHECK,
  DELETING_CHECK_FAILURE,
  CLEAR_CHECK_STATE,
  CLEAR_STATE,
  UPDATE_CURRENT_CHECK_PAGE,
  RESET_CHECK_META
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  receiveMetadata,
  deleteData,
  increment,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const checkSchema = new schema.Entity('checks')
const checkListSchema = new schema.Array(checkSchema)

const state = {
  data: {},
  all: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: false,
  deleting: false,
  deleteFailure: null
}

const actions = {
  fetchChecksWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_CHECKS, true)
    return Vue.http.get(buildUrlWithQuery('checks', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_CHECKS, false)
        this.commit(RESET_CHECK_META)
        if (!(r && r.results)) return this.commit(RECEIVE_CHECK_FAILURE, 'Error fetching checks')
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, checkListSchema)
        if (!result || !Array.isArray(result)) return this.commit(RECEIVE_CHECK_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_CHECKS, { data: entities.checks })
        this.commit(RECEIVE_CHECK_METADATA, { next, previous, count, result: result.length })
        this.commit(UPDATE_CURRENT_CHECK_PAGE, 1)
        return { query, count, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_CHECKS, false)
        this.commit(RECEIVE_CHECK_FAILURE, handleXHRError(e))
      })
  },
  async fetchAllChecksWithQuery (store, query, pageSize = 200) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    if (!query.page_size) query.page_size = pageSize
    try {
      this.commit(RECEIVING_CHECKS, true)
      const url = buildUrlWithQuery('checks', query, instanceUrl)
      const firstPage = await Vue.http.get(url, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!(firstPage && firstPage.results)) throw new Error(`Error fetching all checks`)
      let requests = []
      if (firstPage.count > pageSize) {
        const pageCt = Math.ceil(firstPage.count / pageSize)
        for (let i = 2; i <= pageCt; i++) {
          requests.push(Vue.http.get(`${url}&page=${i}`, { headers: getHeaders(this.getters.getToken) })
            .then(checkStatus)
            .then(parseJSON)
          )
        }
      }
      const nextPages = await Promise.all(requests)
      let allResults = firstPage.results
      if (nextPages && nextPages.length) {
        allResults = [...allResults, ...nextPages.reduce((a, b) => a.concat(b.results), [])]
      }
      this.commit(RECEIVING_CHECKS, false)
      let { entities } = normalize(allResults, checkListSchema)
      for (let i in entities.checks) {
        entities.checks[i].type = 'checks'
      }
      this.commit(RECEIVE_CHECKS, { data: entities.checks })
      return { query, count: firstPage.count, entities }
    } catch (e) {
      console.log(e)
      this.commit(RECEIVING_CHECKS, false)
      return e
    }
  },
  fetchAllChecks (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_CHECKS, true)
    return Vue.http.get(buildUrl('checks?serialize=false', null, instanceUrl), { headers })
      .then(checkStatus)
      .then(response => {
        this.commit(RECEIVING_CHECKS, false)
        if (!response || !Array.isArray(response.body)) return this.commit(RECEIVE_CHECK_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_ALL_CHECKS, { data: response.body })
        return response.body
      })
      .catch(e => {
        this.commit(RECEIVING_CHECKS, false)
        this.commit(RECEIVE_CHECK_FAILURE, e.statusText)
      })
  },
  /**
   * Fetches one or more *printable* APs with the option to pass query string filter parameters
   * @param store
   * @param query
   * @return {Promise<*>}
   */
  fetchPrintableChecksWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    if (query) {
      query.nest_lines = false
    } else {
      query = { nest_lines: false }
    }

    return Vue.http.get(buildUrlWithQuery('checks/printable', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_CHECKS, false)
        this.commit(RESET_CHECK_META)
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, checkListSchema)
          let mutationPayload = { data: entities.checks }
          if (query && query.page && typeof query.page === 'number') {  // If query contains a page number
            mutationPayload.page = query.page                           // pass the page number to the mutation handler
          }
          this.commit(RECEIVE_CHECKS, mutationPayload)
          this.commit(RECEIVE_CHECK_METADATA, { next, previous, count, result: result.length })
          this.commit(UPDATE_CURRENT_CHECK_PAGE, query.page || 1)
          return { query, count, entities }
        }
      })
      .catch(e => {
        this.commit(RECEIVING_CHECKS, false)
        this.commit(RECEIVE_CHECK_FAILURE, handleXHRError(e))
      })
  },
  updateCheck (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(UPDATING_CHECK, id)
    this.commit(UPDATING_CHECK_FAILURE, { id: '', err: '' })
    return Vue.http.patch(buildUrl(`checks/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        this.commit(UPDATING_CHECK, null)
        if (r && r.status === 200) {
          const { entities, result } = normalize(r.body, checkSchema)
          if (!result || (Array.isArray(result) && !result.length)) return this.commit(RECEIVE_CHECK_FAILURE, 'Error normalizing response')
          this.commit(RECEIVE_CHECKS, { data: entities.checks })
          return r.body
        } else {
          this.commit(RECEIVE_CHECK_FAILURE, r ? r.statusText : 'Failed to update Check')
        }
      })
      .catch(e => {
        this.commit(UPDATING_CHECK, null)
        this.commit(UPDATING_CHECK_FAILURE, { id, err: handleXHRError(e) })
      })
  },
  clearCheckState (store, clearCache = false) {
    this.commit(CLEAR_CHECK_STATE, clearCache)
    return true
  },
  resetCheckMeta () {
    this.commit(RESET_CHECK_META)
  }
}

const getters = {
  getChecks: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getCheckById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllChecks: (state) => {
    return state.all
  },
  getAllCachedChecks: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getCheckMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getCheckFetchStatus: (state) => {
    return state.receivingData
  },
  getRelatedTransactionsById: (state, rootState) => (id) => {
    let related = []
    let check = state.data[id]
    if (check) {
      for (let i = 0; i < check.related_transactions.length; i++) {
        const transaction = check.related_transactions[i]
        if (!rootState.getApById(transaction)) continue
        related.push(rootState.getApById(transaction))
      }
    }
    return related
  },
  getCheckByRelatedTransactionId: (state) => (id) => {
    return state.data ? Object.values(state.data).filter(v => v.related_transactions.includes(parseInt(id))) : []
  },
  getCheckSaveStatus: (state) => {
    return state.saving
  },
  getCheckUpdateStatus: (state) => {
    return state.updating
  },
  getCheckDeleteStatus: (state) => {
    return state.deleting
  },
  getCurrentCheckPage: (state) => {
    if (state.currentPage) return state.currentPage
  }
}

const mutations = {
  [RECEIVE_CHECKS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVE_ALL_CHECKS] (state, results) {
    const { data } = results
    state.all = data
  },
  [RECEIVING_CHECKS] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_CHECK_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_CHECK] (state, payload) {
    state.saving = false
  },
  [SAVING_CHECK_FAILURE] (state, payload) {
    state.saveFailure = null
  },
  [UPDATING_CHECK] (state, payload) {
    state.updating = payload
  },
  [UPDATING_CHECK_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_CHECK] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_CHECK] (state, payload) {
    state.deleting = payload
  },
  [DELETING_CHECK_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleting = { id, err }
  },
  [INCREMENT_CHECK_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_CHECK_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [UPDATE_CURRENT_CHECK_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [CLEAR_CHECK_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_CHECK_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/check.js